import dayjs from 'dayjs';
import LessonCard from './LessonCard/LessonCard';
import { Accordion, Badge, Box, Separator, Spacer, Text } from '@chakra-ui/react';
import { formatMonth, formatWeekday } from '@opanuj/shared/utils';
import { getLessonStatusForUid } from '@opanuj/shared/lessons';
import { Lesson, LessonStatus, Weekday } from '@opanuj/shared/types';
import { useSettings } from '../../../hooks/useSettings';
import { useUser } from '../../../hooks/useUser';

const SEASON_MONTHS_ORDER = [8, 9, 10, 11, 0, 1, 2, 3, 4, 5, 6, 7];

const WEEKDAYS: Weekday[] = [
	Weekday.Sun,
	Weekday.Mon,
	Weekday.Tue,
	Weekday.Wed,
	Weekday.Thu,
	Weekday.Fri,
	Weekday.Sat,
];

const getWeekStart = (date: dayjs.Dayjs) =>
	date.startOf('day').subtract((date.day() + 6) % 7, 'day');

function groupLessonsByDay(lessons: Lesson[], specialDayKeys: string[]) {
	const days = new Map<string, Lesson[]>();

	lessons.forEach((lesson) => {
		const date = dayjs(lesson.timestamp * 1000);
		const dayKey = date.format('YYYY-MM-DD');
		const dayLessons = days.get(dayKey) ?? [];

		dayLessons.push(lesson);
		days.set(dayKey, dayLessons);
	});

	specialDayKeys.forEach((dayKey) => {
		if (!days.has(dayKey)) days.set(dayKey, []);
	});

	return [...days.entries()].sort(([first], [second]) => first.localeCompare(second));
}

export type LessonsListProps = {
	lessons: Lesson[];
};

function LessonsList({ lessons }: LessonsListProps) {
	const [user, userLoading, userError] = useUser();
	const [settings] = useSettings();

	if (userLoading || userError || !user) return null;

	const daysOff = settings?.daysOff ?? [];
	const currentDayKey = dayjs().format('YYYY-MM-DD');
	const currentWeekStart = getWeekStart(dayjs());
	const upcomingWeekStart = currentWeekStart.add(7, 'day');
	const currentMonth = dayjs().month();
	const specialDayKeys = [...new Set([...daysOff, currentDayKey])];
	const defaultExpandedMonths = [...new Set([
		currentWeekStart.month(),
		upcomingWeekStart.month(),
	])]
		.filter((month) => SEASON_MONTHS_ORDER.includes(month))
		.map(String);

	const lessonsByMonth = lessons.reduce(
		(acc, l) => {
			const idx = dayjs(l.timestamp * 1000).month();
			acc[idx].lessons.push(l);
			if (getLessonStatusForUid(l, user.uid) === LessonStatus.Cancelled)
				acc[idx].cancelledCount++;
			acc[idx].periodEnd = dayjs(l.timestamp * 1000).endOf('month');
			return acc;
		},
		SEASON_MONTHS_ORDER.reduce(
			(a, i) => ({
				...a,
				[i]: { lessons: [], cancelledCount: 0, periodEnd: dayjs() },
			}),
			{} as {
				[idx: number]: {
					lessons: Lesson[];
					cancelledCount: number;
					periodEnd: dayjs.Dayjs;
				};
			}
		)
	);

	return (
        <>
            <Accordion.Root defaultValue={defaultExpandedMonths} multiple collapsible>
                {SEASON_MONTHS_ORDER.map((monthKey) => {
				const monthLessons = lessonsByMonth[monthKey];
					const monthSpecialDayKeys = specialDayKeys.filter((dayKey) => dayjs(dayKey).month() === monthKey);
				monthLessons.lessons.sort((l1, l2) => l1.timestamp - l2.timestamp);
					if (!monthLessons.lessons.length && !monthSpecialDayKeys.length) {
					return null;
				}

				const accordionValue = String(monthKey);
				return (
                        <Accordion.Item key={monthKey} value={accordionValue}>
                            <h2>
								<Accordion.ItemTrigger>
									<Box as="span" flex="1" textAlign="left" textTransform='uppercase' letterSpacing='wider' color={monthKey === currentMonth ? 'orange.500' : 'gray.600'} fontSize="sm">
										{formatMonth(monthKey)}
									</Box>
									<Spacer />
									{monthLessons.cancelledCount ? (
										<Badge colorPalette='blue' variant='subtle' size='md' p={1} mx={2}>{`Lekcji do odrobienia: ${monthLessons.cancelledCount}`}</Badge>
									) : null}
									<Accordion.ItemIndicator />
								</Accordion.ItemTrigger>
							</h2>
							<Accordion.ItemContent pb={4}><Accordion.ItemBody px={2}>
								{(() => {
									const days = groupLessonsByDay(monthLessons.lessons, monthSpecialDayKeys);

									return (
										<Box>
											{days.map(([dayKey, dayLessons]) => {
												const isCurrentDay = dayKey === currentDayKey;
												const isDayOff = daysOff.includes(dayKey);

												return (
													<Box key={dayKey} mb={3} bg={isDayOff ? 'gray.100' : undefined} borderRadius={isDayOff ? 'md' : undefined} p={isDayOff ? 2 : undefined}>
														<Text textTransform="capitalize" color={isCurrentDay ? 'orange.500' : 'gray.600'} fontSize="sm" mt={2} mb={1}>
															{`${formatWeekday(WEEKDAYS[dayjs(dayKey).day()])}, ${dayjs(dayKey).format('DD.MM.YYYY')}`}
														</Text>
														{isCurrentDay && <Separator borderColor="orange.500" mb={2} />}
														{isDayOff && <Text color="gray.500" fontSize="sm" fontStyle="italic">Dzień wolny</Text>}
														{dayLessons.sort((first, second) => first.timestamp - second.timestamp).map((lesson) => (
															<LessonCard key={lesson.id} lesson={lesson} />
														))}
													</Box>
												);
											})}
										</Box>
									);
								})()}
							</Accordion.ItemBody></Accordion.ItemContent>
                        </Accordion.Item>
                    );
			})}
            </Accordion.Root>
        </>
    );
}

export default LessonsList;
